export interface PortfolioProject {
  id: number;
  title: string;
  category: string;
  description: string;
  tags: string[];
  image: string;
  client: string;
  industry: string;
  duration: string;
  year: string;
  challenge: string;
  solution: string;
  results: { metric: string; value: string }[];
  testimonial?: { text: string; author: string; role: string };
}

export const portfolioProjects: PortfolioProject[] = [
  {
    id: 1,
    title: "FinTech AI Dashboard",
    category: "AI & Analytics",
    description: "Real-time financial analytics platform powered by machine learning for a Chicago-based investment firm.",
    tags: ["React", "Python", "TensorFlow", "AWS"],
    image: "https://readdy.ai/api/search-image?query=modern%20fintech%20dashboard%20interface%20with%20dark%20theme%20charts%20graphs%20data%20visualization%20professional%20clean%20minimal%20design%20on%20dark%20navy%20background&width=600&height=400&seq=port1&orientation=landscape",
    client: "NovaBridge Capital",
    industry: "Financial Services",
    duration: "6 months",
    year: "2024",
    challenge: "NovaBridge Capital was drowning in fragmented data across 14 different systems. Analysts spent 70% of their time manually compiling reports instead of making investment decisions. They needed a unified, real-time intelligence platform.",
    solution: "We built a centralized AI-powered dashboard that ingests live market feeds, internal portfolio data, and macroeconomic indicators. A custom TensorFlow model surfaces predictive signals and anomaly alerts, while the React frontend delivers sub-second query responses across billions of data points.",
    results: [
      { metric: "Decision Speed", value: "2x faster" },
      { metric: "Manual Reporting", value: "Reduced 70%" },
      { metric: "Portfolio Returns", value: "+18% YoY" },
      { metric: "Data Sources Unified", value: "14 systems" },
    ],
    testimonial: {
      text: "CPI Tech transformed our entire data infrastructure. Their AI consulting team delivered a predictive analytics platform that cut our decision-making time in half. Absolutely world-class execution.",
      author: "Marcus Thompson",
      role: "CTO, NovaBridge Capital",
    },
  },
  {
    id: 2,
    title: "HealthCare Mobile App",
    category: "App Development",
    description: "Cross-platform patient management app serving 50,000+ users across 12 hospital networks.",
    tags: ["React Native", "Node.js", "PostgreSQL"],
    image: "https://readdy.ai/api/search-image?query=healthcare%20mobile%20app%20interface%20clean%20white%20medical%20design%20patient%20management%20modern%20UI%20professional%20minimal%20light%20background&width=600&height=420&seq=port2&orientation=landscape",
    client: "MedCore Systems",
    industry: "Healthcare",
    duration: "9 months",
    year: "2023",
    challenge: "MedCore's 12 hospital networks were using 5 different legacy patient management systems that couldn't communicate with each other. Staff wasted hours on duplicate data entry, and patients experienced delays in care coordination.",
    solution: "We designed and built a unified cross-platform mobile app (iOS & Android) with a shared Node.js backend. The app integrates with all existing EHR systems via HL7 FHIR APIs, enabling real-time patient data sync, appointment scheduling, and secure messaging between care teams.",
    results: [
      { metric: "Active Users", value: "50,000+" },
      { metric: "Care Coordination Time", value: "Reduced 45%" },
      { metric: "App Store Rating", value: "4.8 / 5.0" },
      { metric: "Hospital Networks", value: "12 integrated" },
    ],
    testimonial: {
      text: "The mobile app they built for us handles 50,000+ daily active users without a hiccup. Their development team is sharp, communicative, and genuinely cares about the product outcome.",
      author: "Priya Sharma",
      role: "VP of Engineering, MedCore Systems",
    },
  },
  {
    id: 3,
    title: "E-Commerce SEO Campaign",
    category: "Digital Marketing",
    description: "300% organic traffic growth for a national retail brand through advanced SEO and content strategy.",
    tags: ["SEO", "Content", "Analytics", "PPC"],
    image: "https://readdy.ai/api/search-image?query=digital%20marketing%20analytics%20dashboard%20ecommerce%20growth%20charts%20modern%20office%20workspace%20professional%20clean%20minimal%20light%20neutral%20background&width=600&height=380&seq=port3&orientation=landscape",
    client: "RetailEdge Inc.",
    industry: "E-Commerce / Retail",
    duration: "12 months",
    year: "2023",
    challenge: "RetailEdge had strong products but virtually no organic search presence. They were spending $80K/month on paid ads with diminishing returns, and their website had severe technical SEO issues that were suppressing rankings across 2,000+ product pages.",
    solution: "We conducted a full technical SEO audit and rebuilt their site architecture. Our content team produced 150+ high-intent articles and product guides. We restructured their PPC campaigns to focus on bottom-funnel keywords, and implemented a conversion rate optimization program across key landing pages.",
    results: [
      { metric: "Organic Traffic", value: "+300% in 12mo" },
      { metric: "Paid Ad Spend", value: "Reduced 40%" },
      { metric: "Revenue from SEO", value: "+$2.4M" },
      { metric: "Keyword Rankings", value: "1,200 page-1" },
    ],
    testimonial: {
      text: "Our organic traffic tripled in 8 months thanks to CPI's digital marketing strategy. The ROI has been phenomenal, best investment we've made in years.",
      author: "James Kowalski",
      role: "CEO, RetailEdge Inc.",
    },
  },
  {
    id: 4,
    title: "Cloud Security Overhaul",
    category: "Cybersecurity",
    description: "Enterprise-grade zero-trust security architecture for a Fortune 500 logistics company.",
    tags: ["AWS", "Zero Trust", "SIEM", "Compliance"],
    image: "https://readdy.ai/api/search-image?query=cybersecurity%20cloud%20infrastructure%20network%20security%20abstract%20technology%20concept%20professional%20dark%20background%20glowing%20nodes%20connections&width=600&height=440&seq=port4&orientation=landscape",
    client: "LogiFlow Enterprises",
    industry: "Logistics & Supply Chain",
    duration: "8 months",
    year: "2024",
    challenge: "LogiFlow suffered a near-breach incident that exposed critical vulnerabilities in their legacy perimeter-based security model. With 40,000 employees and operations in 22 countries, their attack surface was enormous and their compliance posture was at risk.",
    solution: "We designed and implemented a full zero-trust architecture across their AWS environment. This included identity-based micro-segmentation, a SIEM platform for 24/7 threat monitoring, endpoint detection and response (EDR) across all devices, and a complete SOC 2 Type II compliance program.",
    results: [
      { metric: "Security Incidents", value: "Reduced 92%" },
      { metric: "Threat Detection Time", value: "From 72hrs to 4min" },
      { metric: "Compliance", value: "SOC 2 Type II achieved" },
      { metric: "Countries Secured", value: "22" },
    ],
    testimonial: {
      text: "After a near-breach incident, CPI Tech rebuilt our entire security posture. Their zero-trust implementation is rock solid. We sleep better at night knowing they're our security partner.",
      author: "Sarah Chen",
      role: "CISO, LogiFlow Enterprises",
    },
  },
  {
    id: 5,
    title: "Predictive Analytics Platform",
    category: "Big Data",
    description: "Big data pipeline processing 10M+ daily records for supply chain optimization.",
    tags: ["Spark", "Kafka", "Python", "Tableau"],
    image: "https://readdy.ai/api/search-image?query=big%20data%20analytics%20platform%20visualization%20abstract%20data%20streams%20modern%20technology%20professional%20clean%20minimal%20light%20background%20charts&width=600&height=400&seq=port5&orientation=landscape",
    client: "TrueNorth Retail",
    industry: "Retail & Supply Chain",
    duration: "7 months",
    year: "2023",
    challenge: "TrueNorth was sitting on years of transactional data but had no way to extract actionable insights. Inventory decisions were made on gut feel, leading to $12M in annual overstock and stockout losses.",
    solution: "We built an end-to-end big data pipeline using Apache Spark and Kafka to process 10M+ daily records in real time. A Python-based ML model predicts demand at the SKU level with 94% accuracy. Tableau dashboards give buyers and planners instant visibility into inventory health and reorder signals.",
    results: [
      { metric: "Daily Records Processed", value: "10M+" },
      { metric: "Demand Forecast Accuracy", value: "94%" },
      { metric: "Inventory Losses Reduced", value: "$8.5M saved" },
      { metric: "Pipeline Uptime", value: "99.98%" },
    ],
    testimonial: {
      text: "The big data pipeline CPI built processes millions of records daily with zero downtime. Their team's expertise in Spark and Kafka is genuinely impressive.",
      author: "Linda Vasquez",
      role: "Head of Analytics, TrueNorth Retail",
    },
  },
  {
    id: 6,
    title: "AI Staffing Automation",
    category: "IT Staffing",
    description: "Intelligent talent matching system reducing hiring time by 60% for a global tech recruiter.",
    tags: ["AI", "NLP", "React", "FastAPI"],
    image: "https://readdy.ai/api/search-image?query=HR%20technology%20staffing%20platform%20modern%20office%20team%20collaboration%20professional%20clean%20minimal%20neutral%20background%20workspace%20technology&width=600&height=360&seq=port6&orientation=landscape",
    client: "Midwest Health Network",
    industry: "Healthcare IT",
    duration: "5 months",
    year: "2024",
    challenge: "Midwest Health Network needed to hire 50+ specialized IT engineers within 90 days to support a major EHR migration. Their internal HR team was overwhelmed, and traditional recruiting agencies were delivering poor-fit candidates with 6-8 week lead times.",
    solution: "We deployed our AI-powered talent matching platform, which uses NLP to parse job requirements and candidate profiles, then ranks matches by technical fit, culture alignment, and availability. Our staffing team ran parallel outreach campaigns, and the platform automated screening, scheduling, and feedback loops.",
    results: [
      { metric: "Engineers Placed", value: "52 in 3 weeks" },
      { metric: "Time-to-Hire", value: "Reduced 60%" },
      { metric: "Candidate Quality Score", value: "4.7 / 5.0" },
      { metric: "90-Day Retention", value: "100%" },
    ],
    testimonial: {
      text: "CPI's IT staffing division placed 12 engineers on our team within 3 weeks. Every single one was a perfect fit. Their vetting process is unmatched in the industry.",
      author: "David Okafor",
      role: "Director of IT, Midwest Health Network",
    },
  },
];
